import { useNavigate } from "@tanstack/react-router";
import { formatRelativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { categoryMeta, type NotificationRow } from "./notification-data";

/**
 * One feed row: category icon, title, one-line body preview, relative time, and
 * an unread dot. Clicking marks it read and (when the row carries a link_path)
 * navigates to the deep link, then closes the panel.
 */
export function NotificationItem({
  notification,
  onMarkRead,
  onNavigated,
}: {
  notification: NotificationRow;
  onMarkRead: (id: string) => void;
  onNavigated: () => void;
}) {
  const navigate = useNavigate();
  const { icon: Icon, iconWrapClass } = categoryMeta(notification.category);
  const isUnread = !notification.read_at;

  const handleClick = () => {
    if (isUnread) onMarkRead(notification.notification_id);
    if (notification.link_path) {
      onNavigated();
      // `href`, not `to`: link_path is a raw server-built path that may carry
      // its own query string ("?dt=…"), which `to` does not accept (same rule
      // as RedirectToHref in routes/__root.tsx).
      void navigate({ href: notification.link_path });
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted",
        isUnread && "bg-primary/5",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
          iconWrapClass,
        )}
      >
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className={cn("truncate text-sm text-foreground", isUnread && "font-semibold")}>
            {notification.title}
          </span>
          <time className="shrink-0 text-xs text-muted-foreground">
            {formatRelativeTime(notification.created_at)}
          </time>
        </span>
        {notification.body && (
          <span className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
            {notification.body}
          </span>
        )}
      </span>
      {isUnread && <span aria-hidden className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />}
    </button>
  );
}
